/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useRef, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import Shimmer from '../../../../shared/_TBComponents/Shimmer';
import Text from '../../../../shared/components/Text/Text';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';

import {
  saveCalendarExercise,
  saveNonDateBasedWorkoutExercise,
  updateProgrammedCalendarItem,
  updateSavedWorkoutItem,
  swapProgrammedItemOnChildCalendar,
} from '../../ducks/calendarActions';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  visibility: ${(props) => (props.hidden ? 'hidden' : 'visible')};
`;

const SessionBreakInput = styled('input')`
  border: none;
  border-radius: 4px;
  max-height: 34px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;


   &::placeholder {
    font-family: 'Nunito Sans', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.textTertiary};
  }

   &:focus {
    background: ${({ theme }) => theme.colors.surface};
    outline: none; /* optional: removes default blue outline */
    color: ${({ theme }) => theme.colors.text};
  }
`;

const SessionBreakInputShimmer = styled('input')`
  width: 100%;
  border: none;
  border-radius: 5px;
  min-height: 34px;
  max-height: 34px;
`;

const NameAndInputContainer = styled('div')`
  display: flex;
  gap: 8px;
  align-items: center;
  .checkbox-input {
    appearance: none; /* Remove default checkbox styling */
    min-width: 18px;
    min-height: 18px;
    max-width: 18px;
    max-height: 18px;
    border: 2px solid ${({ theme }) => theme.colors.borderMedium};
    border-radius: 4px;
    outline: none;
    cursor: ${(props) => (props.cursorNotAllowed ? 'not-allowed' : 'pointer')};
    position: relative;
    transition: ${TransitionGlobal};
    :hover {
      opacity: 0.7;
    }
  }
  .checkbox-input:checked {
    border-color: ${({ theme }) => theme.colors.hover}; /* blue border when checked */
  }

  .checkbox-input:checked::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 5px;
    width: 5px;
    height: 10px;
    border: solid ${({ theme }) => theme.colors.hover}; /* blue checkmark */
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: block;
  }
`;

const alreadySavedGlobal = new Set();

/** Extra px past the last character that still counts as "on the text",
 * so a click just after the final letter places the cursor at the end. */
const CLICK_PAST_TEXT_SLOP = 8;
let textMeasureContext = null;

/** The title input stretches the full row, so a click in the empty space
 * past the title should close the editor like an outside click. Clicks on
 * or between characters return false so the coach can place the cursor.
 * Falls back to false (stay open) when canvas measuring isn't available. */
const isClickPastInputText = (event) => {
  const input = event.currentTarget;
  if (!textMeasureContext) {
    textMeasureContext = document.createElement('canvas').getContext('2d');
  }
  if (!textMeasureContext) {
    return false;
  }
  const style = window.getComputedStyle(input);
  textMeasureContext.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  const textWidth = textMeasureContext.measureText(input.value || input.placeholder).width;
  const clickX = event.clientX
    - input.getBoundingClientRect().left
    - (parseFloat(style.paddingLeft) || 0)
    + input.scrollLeft;
  return clickX > textWidth + CLICK_PAST_TEXT_SLOP;
};

const SessionBreakTable = ({
  contentIndex,
  nonSwappedItemFromParentCalendar,
  content,
  isRecurring,
  checkBoxInput,
  setSessionBreakInputOpen,
  sessionBreakInputValue,
  setSessionBreakInputValue,
  inputBox,
  date,
  dayNumber,
  hidden,
  selectTitleOnOpen,
}) => {
  const inputRef = useRef(null);
  const [isAdding, setIsAdding] = useState(false);
  const [collapseWorkoutItems, setCollaseWorkoutItems] = useState(!content?.showSessionBreakItems);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const programType = useSelector((state) => state.calendar.data.programType);
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;

  const dispatch = useDispatch();

  const errorAddReset = () => {
    setIsAdding(false);
  };

  const handleAutoSaveCalendarSessionBreak = (contentIndexParam) => {
    setIsAdding(true);
    const submissionObject = {
      /** Conditional key based on programType: "date" OR "dayNumber" */
      [programType === 'free-form-program' ? 'dayNumber' : 'date']:
          programType === 'free-form-program'
            ? dayNumber
            : moment(effectiveDate).format('YYYY-MM-DD'),
      type: 'session_break',
      showSessionBreakItems: true,
    };
    if (content.description) {
      submissionObject.description = content.description;
    }
    const formattedDate = moment(effectiveDate).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      dispatch(saveNonDateBasedWorkoutExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        dayNumber,
        errorAddReset,
        { onSuccess: () => setIsAdding(false) },
        content.assignedId,
      ));
    } else {
      dispatch(saveCalendarExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        formattedDate,
        errorAddReset,
        { onSuccess: () => setIsAdding(false) },
        content.assignedId,
      ));
    }
  };

  const debouncedHandleUpdateSessionBreak = useRef(
    debounce((
      dbSessionBreakInputValue,
      dbNonSwappedItemFromParentCalendar,
      dbCollapseWorkoutItems,
      reorder = false,
    ) => {
      const updateItem = {
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : moment(effectiveDate).format('YYYY-MM-DD'),
        order: contentIndex,
        description: content?.exercise?.description || '',
        type: 'session_break',
        showSessionBreakItems: !dbCollapseWorkoutItems,
      };
      if (programType === 'free-form-program') {
        updateItem.dayNumber = dayNumber;
      }
      if (programType !== 'free-form-program') {
        updateItem.isRecurring = isRecurring;
      }
      if (dbSessionBreakInputValue) {
        updateItem.title = dbSessionBreakInputValue;
      }
      if (programType === 'free-form-program') {
        dispatch(updateSavedWorkoutItem(currentUser.accountCode, updateItem, contentIndex, dayNumber, reorder, {
          onSuccess: () => {
            setSessionBreakInputOpen(false);
          },
          onError: () => {},
        }));
      }
      if (programType === 'calendar') {
        if (dbNonSwappedItemFromParentCalendar) {
          dispatch(swapProgrammedItemOnChildCalendar(currentUser.accountCode, { ...updateItem }, contentIndex, moment(effectiveDate).format('YYYY-MM-DD'), reorder, {
            onSuccess: () => {
              setSessionBreakInputOpen(false);
            },
            onError: () => {},
          }));
        } else {
          dispatch(updateProgrammedCalendarItem(currentUser.accountCode, updateItem, contentIndex, moment(effectiveDate).format('YYYY-MM-DD'), reorder, {
            onSuccess: () => {
              setSessionBreakInputOpen(false);
            },
            onError: () => {},
          }));
        }
      }
    }, 2000),
  ).current;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      debouncedHandleUpdateSessionBreak(sessionBreakInputValue, nonSwappedItemFromParentCalendar, collapseWorkoutItems);
      /** Force the debounced update to run immediately */
      debouncedHandleUpdateSessionBreak.flush();

      e.target.blur();
      setSessionBreakInputOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      /** The "collapse workout items" checkbox is a sibling SessionBreakTable
       * instance — let its onChange own that flow without tearing down this
       * editor mid-toggle. */
      if (event.target?.name === 'collapse workout item') {
        return;
      }
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        inputRef.current.blur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /** the CellMeasurer wrapped around each row in the virtualized list
   * can cause remounting of the item a few times as it measures
   * the "just in time" height, this can cause multiple firings of this
   * handleAutoSave funtion (which is a problem because it will create 3 of the same items in the data base).
   * To get around that we save the item by id
   * in the global set that will persist renders and if we have alreay saved
   * the item them we will not fire that function again.
   */
  useEffect(() => {
    if (content.preSave && inputBox && !alreadySavedGlobal.has(content.assignedId)) {
      alreadySavedGlobal.add(content.assignedId);
      handleAutoSaveCalendarSessionBreak(contentIndex);
    }
  }, [content.assignedId]);

  /** The title arrives pre-filled with the default for a break the coach just
   * added, and autoFocus would leave the caret after it — select the whole
   * thing so the first keystroke replaces it. Deliberately mount-only: a
   * later focus is the coach clicking back into the title, where blowing away
   * his cursor position would be wrong. */
  useEffect(() => {
    if (!selectTitleOnOpen || checkBoxInput) return;
    inputRef.current?.select();
  }, []);

  const setSessionBreakInputValueFunction = (value) => {
    setSessionBreakInputValue(value);
  };

  return (
    <>
      {checkBoxInput && !isTvMode ? (
        <Container hidden={hidden}>
          {!isAdding ? (
            <NameAndInputContainer>
              <input
                className='checkbox-input'
                type='checkbox'
                name='collapse workout item'
                onChange={(e) => {
                  setCollaseWorkoutItems(!collapseWorkoutItems);
                  const title = sessionBreakInputValue || content?.title;
                  debouncedHandleUpdateSessionBreak(title, nonSwappedItemFromParentCalendar, e.target.checked);
                  debouncedHandleUpdateSessionBreak.flush();
                }}
                checked={collapseWorkoutItems}
              />
              <Text fontSize='16px' style={{ whiteSpace: 'nowrap ' }}>Collapse workout items under session break for athletes</Text>
            </NameAndInputContainer>
          ) : (
            <Shimmer>
              <SessionBreakInputShimmer />
            </Shimmer>
          )}
        </Container>
      ) : checkBoxInput ? null : (
        <Container hidden={hidden}>
          {!isAdding ? (
            <SessionBreakInput
              ref={inputRef}
              autoFocus
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                if (isClickPastInputText(e)) {
                  /** Blur instead of closing directly so onBlur saves the title */
                  e.preventDefault();
                  inputRef.current.blur();
                }
              }}
              onChange={(e) => {
                setSessionBreakInputValueFunction(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                debouncedHandleUpdateSessionBreak(
                  sessionBreakInputValue,
                  nonSwappedItemFromParentCalendar,
                  collapseWorkoutItems,
                );
                debouncedHandleUpdateSessionBreak.flush();
                setSessionBreakInputOpen(false);
              }}
              value={sessionBreakInputValue}
              placeholder='Add Session Break Title'
            />
          ) : (
            <Shimmer>
              <SessionBreakInputShimmer />
            </Shimmer>
          )}
        </Container>
      )}
    </>
  );
};

SessionBreakTable.propTypes = {
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  checkBoxInput: PropTypes.bool,
  setSessionBreakInputOpen: PropTypes.func,
  inputBox: PropTypes.bool,
  date: PropTypes.string,
  /** Free-form programs are keyed by day number; DraggableContent reads it
   * off redux, where it is a number. */
  dayNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hidden: PropTypes.bool,
  sessionBreakInputValue: PropTypes.string,
  setSessionBreakInputValue: PropTypes.func,
  isRecurring: PropTypes.string,
  selectTitleOnOpen: PropTypes.bool,
};

SessionBreakTable.defaultProps = {
  checkBoxInput: false,
  inputBox: false,
  setSessionBreakInputOpen: () => {},
  date: null,
  dayNumber: null,
  hidden: false,
  sessionBreakInputValue: '',
  setSessionBreakInputValue: () => {},
  isRecurring: null,
  selectTitleOnOpen: false,
};

export default SessionBreakTable;
